// src/components/NotificationBell.js
// Bell icon with unread count badge + dropdown panel

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCheck, AlertTriangle, CheckCircle, Info, Clock, X } from 'lucide-react';
import * as awsApi from '../services/awsApiService';

const SEVERITY_STYLES = {
  success: { bg: 'bg-green-50',  border: 'border-green-200',  icon: CheckCircle,    iconColor: 'text-green-500'  },
  error:   { bg: 'bg-red-50',    border: 'border-red-200',    icon: AlertTriangle,  iconColor: 'text-red-500'    },
  warning: { bg: 'bg-amber-50',  border: 'border-amber-200',  icon: AlertTriangle,  iconColor: 'text-amber-500'  },
  info:    { bg: 'bg-blue-50',   border: 'border-blue-200',   icon: Info,           iconColor: 'text-blue-500'   },
};

const timeAgo = (d) => {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    const res = await awsApi.getNotifications();
    if (res.success) {
      setNotifications(res.data);
      setUnread(res.unreadCount);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000); // poll every minute
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    await awsApi.markNotificationRead(id);
    setNotifications(n => n.map(x => x.id === id ? { ...x, read: true } : x));
    setUnread(u => Math.max(0, u - 1));
  };

  const handleMarkAll = async () => {
    await awsApi.markAllNotificationsRead();
    setNotifications(n => n.map(x => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
        title="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-slate-600" />
              <span className="font-semibold text-slate-800 text-sm">Notifications</span>
              {unread > 0 && (
                <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">{unread} new</span>
              )}
            </div>
            {unread > 0 && (
              <button onClick={handleMarkAll} className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1 font-medium">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-slate-400">
                <Bell size={24} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => {
                const style = SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.info;
                const Icon = style.icon;
                return (
                  <div key={n.id}
                    className={`px-4 py-3 border-b border-slate-50 last:border-0 flex gap-3 ${!n.read ? 'bg-blue-50/40' : 'bg-white'}`}>
                    <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${style.bg}`}>
                      <Icon size={13} className={style.iconColor} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold leading-tight ${!n.read ? 'text-slate-900' : 'text-slate-700'}`}>{n.title}</p>
                      {n.message && <p className="text-xs text-slate-500 mt-0.5 leading-snug">{n.message}</p>}
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Clock size={10} />{timeAgo(n.created_at)}
                      </p>
                    </div>
                    {!n.read && (
                      <button onClick={(e) => handleMarkRead(n.id, e)}
                        className="flex-shrink-0 text-slate-300 hover:text-slate-500 mt-0.5">
                        <X size={13} />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
